'use client';

import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FaHouseChimney,
  FaScrewdriverWrench,
  FaCalendarDay,
  FaUser,
  FaList,
} from 'react-icons/fa6';
import { useStepNavigation } from '../StepNavigationContext';
import { useAutoNavigateToStep } from '../hooks/useAutoNavigateToStep';
import { useScheduler } from '../SchedulerContext';
import { useStartOver } from '../hooks/useStartOver';
import {
  SchedulerScreen,
  useTrackSchedulerScreen,
} from '../hooks/useTrackSchedulerScreen';
import ServicesStep from '../services/ServicesStep';
import {
  Bundle,
  Service,
  ContactRoleType,
  RequiredInfo,
  ServWithMods,
  Company,
} from '@/util/types/serverTypes';
// import Property from '../property/PropertyFinder';
import PropertyStep from '../property/PropertyStep';
import SlotsStep from '../slots/SlotsStep';
import AdditionalContactsStep from '../contacts/AdditionalContactsStep';
import SummaryStep from '../summary/SummaryStep';
import EscapeHatchPage from './EscapeHatchPage';
import { checkEscapeHatch } from '../utils/checkEscapeHatch';
import type { EscapeHatchReason } from '../utils/checkEscapeHatch';

type StepContentProps = {
  step: number;
  step3EscapeReason: EscapeHatchReason | null;
  services: ServWithMods[];
  bundles: Bundle[];
  contactRoles: ContactRoleType[];
  allRequiredInfo: RequiredInfo[];
  company: Company;
};

const StepContent = ({
  step,
  step3EscapeReason,
  services,
  bundles,
  contactRoles,
  allRequiredInfo,
  company,
}: StepContentProps) => {
  const { setEscapeHatchReason } = useScheduler();

  // Create start over handler for escape hatch page
  const handleStartOver = useStartOver({
    navigateToStep: 1,
    deferNavigation: true,
    onBeforeReset: () => {
      // Clear escape hatch reason
      setEscapeHatchReason(null);
    },
    clearDraft: false, // Preserve draft quote
  });

  const getStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className='min-h-[600px]'>
            <ServicesStep
              services={services}
              bundles={bundles}
              contactRoles={contactRoles}
            />
          </div>
        );
      case 2:
        return (
          <div className='min-h-[600px]'>
            <PropertyStep
              allServices={services}
              bundles={bundles}
              allRequiredInfo={allRequiredInfo}
            />
          </div>
        );
      case 3:
        // Show escape hatch page when reason is set (computed synchronously to prevent bypass)
        if (step3EscapeReason) {
          return (
            <EscapeHatchPage
              reason={step3EscapeReason}
              company={company}
              onStartOver={handleStartOver}
            />
          );
        }
        return (
          <div className='min-h-[600px]'>
            <SlotsStep allServices={services} allBundles={bundles} />
          </div>
        );
      case 4:
        return (
          <div className='min-h-[600px]'>
            <AdditionalContactsStep
              services={services}
              bundles={bundles}
              contactRoles={contactRoles}
            />
          </div>
        );
      case 5:
        return (
          <div className='min-h-[600px]'>
            <SummaryStep
              services={services}
              bundles={bundles}
              contactRoles={contactRoles}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return <div className='w-full'>{getStepContent()}</div>;
};

/**
 * Screen reported for the main steps that have no sub-steps. Services and
 * Property report their own screens through `InternalStepper`.
 */
const getTrackedScreen = (
  step: number,
  escapeReason: EscapeHatchReason
): SchedulerScreen | null => {
  switch (step) {
    case 3:
      return escapeReason
        ? { step: 'schedule', screen: 'escape_hatch', detail: escapeReason }
        : { step: 'schedule', screen: 'time_slots' };
    case 4:
      return { step: 'contact', screen: 'additional_contacts' };
    case 5:
      return { step: 'summary', screen: 'summary' };
    default:
      return null;
  }
};

type SchedulerContentProps = {
  services: ServWithMods[];
  bundles: Bundle[];
  contactRoles: ContactRoleType[];
  allRequiredInfo: RequiredInfo[];
  company: Company;
};

export default function SchedulerContent({
  services,
  bundles,
  contactRoles,
  allRequiredInfo,
  company,
}: SchedulerContentProps) {
  const { activeStep, direction } = useStepNavigation();
  const {
    escapeHatchReason,
    setEscapeHatchReason,
    selectedServiceIds,
    containingServiceAreaArray,
    address,
    scheduleSettings,
    isLoadingDraft,
  } = useScheduler();

  // Auto-navigate to correct step based on context state (for draft resume)
  useAutoNavigateToStep();

  // Check for escape hatch conditions when entering step 3 (Slots/Schedule)
  // This catches both normal flow and draft resume scenarios
  useEffect(() => {
    // Wait for draft loading to complete before checking
    // This ensures all draft data is fully hydrated before we evaluate escape hatch conditions
    if (isLoadingDraft) {
      return;
    }

    // Only check if we're on step 3 and haven't already set an escape hatch reason
    if (activeStep !== 3 || escapeHatchReason !== null) {
      return;
    }

    console.log(
      '[SchedulerContent] Checking escape hatch conditions on step 3 entry'
    );

    // Run escape hatch check with current context state
    const reason = checkEscapeHatch({
      selectedServiceIds,
      services,
      containingServiceAreaArray,
      address,
      maxSqftOnline: scheduleSettings.maxSqftOnline,
    });

    if (reason) {
      console.log(
        '[SchedulerContent] Escape hatch triggered on step 3 entry:',
        reason
      );
      setEscapeHatchReason(reason);
    }
  }, [
    activeStep,
    escapeHatchReason,
    isLoadingDraft,
    selectedServiceIds,
    services,
    containingServiceAreaArray,
    address,
    scheduleSettings.maxSqftOnline,
    setEscapeHatchReason,
  ]);

  // Compute escape hatch reason synchronously for step 3 so the first paint never shows slots when over max sqft
  const step3EscapeReason =
    activeStep === 3
      ? escapeHatchReason ??
        checkEscapeHatch({
          selectedServiceIds,
          services,
          containingServiceAreaArray,
          address,
          maxSqftOnline: scheduleSettings.maxSqftOnline,
        })
      : null;

  useTrackSchedulerScreen(getTrackedScreen(activeStep, step3EscapeReason));

  // Clear escape hatch reason when navigating away from step 3
  // This allows users to fix the issue and try again
  useEffect(() => {
    if (activeStep !== 3 && escapeHatchReason !== null) {
      console.log(
        '[SchedulerContent] Clearing escape hatch reason - navigated away from step 3'
      );
      setEscapeHatchReason(null);
    }
  }, [activeStep, escapeHatchReason, setEscapeHatchReason]);

  const variants = {
    enter: (direction: string) => ({
      x: direction === 'forward' ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: string) => ({
      x: direction === 'forward' ? '-100%' : '100%',
      opacity: 0,
    }),
  };

  return (
    <div className='flex justify-center p-3 pb-5 sm:p-8'>
      <div className='w-full'>
        <AnimatePresence mode='wait' custom={direction}>
          <motion.div
            key={activeStep}
            custom={direction}
            variants={variants}
            initial='enter'
            animate='center'
            exit='exit'
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <StepContent
              step={activeStep}
              step3EscapeReason={step3EscapeReason}
              services={services}
              bundles={bundles}
              contactRoles={contactRoles}
              allRequiredInfo={allRequiredInfo}
              company={company}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
